document.addEventListener("DOMContentLoaded", () => {
  /** =========================
   * Utility Functions
   * ========================= */
  const loadImage = (src, onSuccess, onError) => {
    const img = new Image();
    img.src = src;
    img.onload = () => onSuccess?.(src);
    img.onerror = () => {
      console.error(`Failed to load image: ${src}`);
      onError?.();
    };
  };

  const setBackgroundImage = (element, src, placement, altClasses) => {
    if (!element || !src) return;
    loadImage(src, () => {
      element.style.backgroundImage = `url(${src})`;
      element.classList.remove(...altClasses);
      element.classList.add(placement);
      console.log(`Set background image: ${src}, placement: ${placement}`);
    });
  };

  const clearActiveAndSet = (buttons, activeButton) => {
    buttons.forEach(btn => btn.classList.remove("active"));
    if (activeButton) activeButton.classList.add("active");
  };

  /** =========================
   * Cached DOM Elements
   * ========================= */
  const mediaGallery       = document.querySelector("media-gallery");
  const mediaItems         = [...document.querySelectorAll(".product__media-item")];
  const frontThumbContainer = document.querySelector(".thumb-container--custom");
  const backThumbContainer  = document.querySelector(".product__media-item:nth-child(2) .thumb-container--back--custom");
  const frontButtonGroups   = [...document.querySelectorAll(".button-group--custom")];
  const backButtonContainer = document.querySelector(".patter-container--back--custom");
  const backTextContainer   = document.querySelector(".text-container--back--custom");
  const frontImgBoxes       = [...document.querySelectorAll(".img--box--custom")];
  const backImgBoxes        = [...document.querySelectorAll(".img--box--back--custom")];
  const backButtonGroupWrappers = [...document.querySelectorAll(".back-button-group-wrapper")];

  const frontInput          = document.querySelector("#front-design-input");
  const backInput           = document.querySelector("#back-design-input");
  const frontPlacementInput = document.querySelector("#front-placement-input");
  const backPlacementInput  = document.querySelector("#back-placement-input");


  if (!mediaGallery) {
    console.error("Media gallery element not found. Selector: media-gallery");
    return;
  }

  console.log(`Found ${mediaItems.length} media items`);

  /** =========================
   * Core Functions
   * ========================= */
  const setMainPreviewImage = (index) => {
    const item = mediaItems[index];
    if (!item) return console.error(`No media item at index ${index}`);
    const mediaId = item.dataset.mediaId;
    try {
      mediaGallery.setActiveMedia(mediaId, false);
      console.log(`Preview set to index ${index}, mediaId: ${mediaId}`);
    } catch (e) {
      console.error(`Error setting preview for mediaId ${mediaId}`, e);
    }
  };

  const updateBackButtons = (blockId, setInitial = false, placement = "chest") => {
    if (!backButtonContainer || !backTextContainer) return;

    // Hide all back button group wrappers
    backButtonGroupWrappers.forEach(wrapper => {
      wrapper.style.display = "none";
    });

    // Show the wrapper for the current blockId
    const activeWrapper = backButtonGroupWrappers.find(wrapper => wrapper.dataset.groupId === blockId);
    if (activeWrapper) {
      activeWrapper.style.display = "flex";
    } else {
      console.warn(`No back button group wrapper found for blockId: ${blockId}`);
    }

    // Hide or show back text container based on wrapper availability
    backTextContainer.style.display = activeWrapper && activeWrapper.querySelector(".button-group--back--custom") ? "block" : "none";

    backButtonContainer.dataset.blockId = blockId;

    const backButtons = activeWrapper ? [...activeWrapper.querySelectorAll(".button-group--back--custom")] : [];

    // Set initial active state
    if (setInitial && backButtons.length > 0) {
      const firstButton = backButtons[0];
      clearActiveAndSet(backButtons, firstButton);
      const backImg = backImgBoxes.find(
        box => box.dataset.blockId === blockId && box.dataset.backId === firstButton.dataset.backId
      );
      if (backImg && backImg.dataset.src) {
        setBackgroundImage(
          backThumbContainer,
          backImg.dataset.src, // Use back_image.value for preview
          placement === "center" ? "top" : "center",
          ["top", "center"]
        );
      } else {
        // Clear back preview if no valid image
        if (backThumbContainer) {
          backThumbContainer.style.backgroundImage = "";
          backThumbContainer.classList.remove("top", "center");
        }
      }
    } else if (!backButtons.length) {
      // Clear back preview if no buttons are available
      if (backThumbContainer) {
        backThumbContainer.style.backgroundImage = "";
        backThumbContainer.classList.remove("top", "center");
      }
    }

    // Event binding for back buttons
    backButtons.forEach(button => {
      // Remove existing listeners to prevent duplicates
      button.removeEventListener("click", button._clickHandler);
      button._clickHandler = () => {
        clearActiveAndSet(backButtons, button);
        const backImg = backImgBoxes.find(
          box => box.dataset.blockId === blockId && box.dataset.backId === button.dataset.backId
        );
        if (backImg && backImg.dataset.src) {
          setBackgroundImage(
            backThumbContainer,
            backImg.dataset.src, // Use back_image.value for preview
            placement === "center" ? "top" : "center",
            ["top", "center"]
          );
        } else {
          // Clear back preview if no valid image
          if (backThumbContainer) {
            backThumbContainer.style.backgroundImage = "";
            backThumbContainer.classList.remove("top", "center");
          }
        }
        setMainPreviewImage(1);
        updateInputsFromUI(); // Sync hidden inputs
      };
      button.addEventListener("click", button._clickHandler);
    });
  };

  const updateBackLabel = (placement) => {
    if (!backTextContainer || backTextContainer.style.display === "none") return;
    const label = placement === "center" ? "SELECT BACK DESIGN" : "SELECT BACK DESIGN";
    // const label = placement === "center" ? "Select Back Logo for Top" : "Select Back Logo for Center";
    backTextContainer.textContent = label;
    backTextContainer.dataset.placement = placement;
    console.log(`Updated back label: ${label}`);
  };

  const setFrontImage = (blockId, placementFallback = "chest") => {
    const frontImg = frontImgBoxes.find(
      box => box.dataset.blockId === blockId && box.dataset.target === "thumb-container--custom"
    );
    if (frontImg?.dataset.src) {
      setBackgroundImage(frontThumbContainer, frontImg.dataset.src, frontImg.dataset.placement, ["chest", "center"]);
      updateBackButtons(blockId, true, frontImg.dataset.placement);
      updateBackLabel(frontImg.dataset.placement);
    } else {
      updateBackButtons(blockId, true, placementFallback);
      updateBackLabel(placementFallback);
    }
  };

  /** =========================
   * Hidden Inputs
   * ========================= */
  // const updateInputs = (frontName, backName, frontPlacement) => {
  //   if (frontInput) frontInput.value = frontName || "";
  //   if (backInput) backInput.value = backName || "";

  //   const backPlacement = frontPlacement === "chest" ? "center" : "top";
  //   if (placementInput) {
  //     placementInput.value = `Front: ${frontPlacement}, Back: ${backPlacement}`;
  //   }

  //   console.log("Updated inputs →", {
  //     front: frontName,
  //     back: backName,
  //     frontPlacement,
  //     backPlacement
  //   });
  // };


const updateInputs = (frontName, backName, frontPlacement) => {
  if (frontInput) frontInput.value = frontName || "";
  if (backInput) backInput.value = backName || "";

  // Derive backPlacement from frontPlacement
  const backPlacement = frontPlacement === "chest" ? "center" : "top";

  if (frontPlacementInput) frontPlacementInput.value = frontPlacement;
  if (backPlacementInput) backPlacementInput.value = backPlacement;

  // Capture front preview image
  const activeFrontImgBox = document.querySelector(
    `.img--box--custom[data-block-id="${document.querySelector(".button-group--custom.active")?.dataset.blockId}"]`
  );
  if (activeFrontImgBox && activeFrontImgBox.dataset.src) {
    const frontDesignImageInput = document.querySelector("#front-design-image-input");
    if (frontDesignImageInput) {
      frontDesignImageInput.value = activeFrontImgBox.dataset.src;
    }
  }

  // Capture back preview image
  const activeBackButton = document.querySelector(".button-group--back--custom.active");
  const activeBackImgBox = activeBackButton
    ? document.querySelector(
        `.img--box--back--custom[data-block-id="${backButtonContainer?.dataset.blockId}"][data-back-id="${activeBackButton?.dataset.backId}"]`
      )
    : null;
  if (activeBackImgBox && activeBackImgBox.dataset.src) {
    const backDesignImageInput = document.querySelector("#back-design-image-input");
    if (backDesignImageInput) {
      backDesignImageInput.value = activeBackImgBox.dataset.src;
    }
  } else {
    const backDesignImageInput = document.querySelector("#back-design-image-input");
    if (backDesignImageInput) {
      backDesignImageInput.value = ""; // Clear if no valid back image
    }
  }

  console.log("Updated inputs →", {
    front: frontName,
    back: backName,
    frontPlacement,
    backPlacement,
    frontDesignImage: activeFrontImgBox?.dataset.src || "",
    backDesignImage: activeBackImgBox?.dataset.src || ""
  });
};


const updateInputsFromUI = () => {
  const activeFront = document.querySelector(".button-group--custom.active");
  const activeBack = backButtonContainer?.querySelector(".button-group--back--custom.active");
  const frontName = activeFront?.dataset.blockName || "";
  const frontPlacement = activeFront?.dataset.placement || "chest";
  let backName = "";

  // Derive backName from active back image box
  if (activeBack) {
    const activeBackImgBox = document.querySelector(
      `.img--box--back--custom[data-block-id="${backButtonContainer?.dataset.blockId}"][data-back-id="${activeBack.dataset.backId}"]`
    );
    if (activeBackImgBox?.dataset.src) {
      // Extract backName from the image source (same as _Back Design Image)
      const src = activeBackImgBox.dataset.src;
      backName = src.split('/').pop().split('?')[0].split('.')[0]; // Extract filename without extension or query
    }
  }

  updateInputs(frontName, backName, frontPlacement);
};

  /** =========================
   * Initialization
   * ========================= */
  if (frontButtonGroups.length > 0) {
    const firstBtn = frontButtonGroups[0];
    firstBtn.classList.add("active");
    console.log(`Initial front block: ${firstBtn.dataset.blockId}`);

    setFrontImage(firstBtn.dataset.blockId);
    setMainPreviewImage(0);
    updateInputsFromUI();
  }

  /** =========================
   * Event Listeners
   * ========================= */
  frontButtonGroups.forEach(btn => {
    btn.addEventListener("click", () => {
      clearActiveAndSet(frontButtonGroups, btn);
      console.log(`Clicked front button blockId: ${btn.dataset.blockId}`);
      setFrontImage(btn.dataset.blockId);
      setMainPreviewImage(0);
      updateInputsFromUI();
    });
  });
});